/** ***********************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 ************************************************************************* */

import PDP from '../../../stores/appPDP';
import Apps from '../../../stores/apps';
import { ACTION_ID } from './appModelConstants';
import AppConfig from '../../../stores/config';

const ApplicableActionFilter = [
    ACTION_ID.NOT_COMPATIBLE,
    ACTION_ID.START_TRIAL,
    ACTION_ID.OPEN,
    ACTION_ID.BUY_NOW,
    ACTION_ID.LIST_OTHER_VERSIONS,
    ACTION_ID.UNINSTALL,
    ACTION_ID.LAUNCH_SERVICE,
    ACTION_ID.UPDATE,
    ACTION_ID.INSTALL,
    ACTION_ID.TRY,
    ACTION_ID.GET_LINK,
    ACTION_ID.RETRY,
    ACTION_ID.BUY,
];

const ACTION_TYPE_MAP = {
    'not-compatible': {
        variant: 'primary',
        quiet: true,
        icon: 'error',
    },
    startTrial: {
        variant: 'cta',
        quiet: false,
    },
    open: {
        variant: 'cta',
        quiet: true,
    },
    'buy-now': {
        variant: 'cta',
        quiet: false,
    },
    buy: {
        variant: 'cta',
        quiet: false,
    },
    update: {
        variant: 'cta',
        quiet: false,
    },
    install: {
        variant: 'cta',
        quiet: false,
    },
    try: {
        variant: 'primary',
	    quiet: true,
    },
    launchService: {
        variant: 'cta',
        quiet: false,
    },
    getLink: {
        variant: 'cta',
        quiet: false,
    },
    retry: {
        variant: 'cta',
        quiet: false,
    },
    uninstall: {
        textType: 'error',
    },
};

class PDPModelMapper {
	/**
	 * toPDPModel function
	 * Map inApp/parsed Contentful data to PDPModel
	 * @param {contentfulPDPItems, controllerData, eventHandlerCallback}
	 * @return {object}
	 */
	toPDPModel = ({
	    app, bannerConfig, actionHandlerCallback, contentfulData, actionList,
	}) => {
	    const applicableActionList = actionList || this.getApplicableActionList({ appObject: app });
	    const PDPModel = new PDP({
	        app,
	        actionHandlerCallback,
	        bannerConfig,
	        contentfulData,
	        applicableActionList,
	        locale: Apps.locale,
	        localAssetsBasePath: AppConfig.pdpAssetsBasePath,
	    });

	    return PDPModel;
	}

	getApplicableActionList({ appObject }) {
	    if (appObject.actionList === undefined || appObject.actionList.length === 0) {
	        return [];
	    }
	    const appActionList = [...appObject.actionList];
	    const applicableActionList = appActionList
		    .filter(({ actionId, actionType }) => (ApplicableActionFilter
	            .includes(actionId) && actionType
				    && Object.prototype.hasOwnProperty.call(actionType, 'component')));

	    const modifiedActionList = applicableActionList.map((action) => {
	        if (Object.prototype.hasOwnProperty.call(ACTION_TYPE_MAP, action.actionId)) {
	            // eslint-disable-next-line
				action.actionType = { ...action.actionType, ...ACTION_TYPE_MAP[action.actionId] };
	        }
	        return action;
	    });

	    if (Apps.isMultipleVersionsAvailable(appObject.sapcode)) {
	        const openActions = modifiedActionList
			    .filter(action => action.actionId === ACTION_ID.OPEN);
	        if (openActions.length
				    && !openActions[0].localizedText.includes(appObject.appFamilyName)) {
	            const displayVersion = appObject.installedVersion
				    ? appObject.installedVersion : appObject.codexVersion;
	            openActions[0].localizedText = `
				    ${openActions[0].localizedText} ${appObject.appFamilyName} (${displayVersion})`;
	        }
	    }

	    return modifiedActionList;
	}

	refreshAppModelData = (pdpModel, { appObject }) => {
	    pdpModel.setApp(appObject);
	    const applicableActionList = this.getApplicableActionList({ appObject });
	    if (applicableActionList) {
	        pdpModel.setApplicableActionList(applicableActionList);
	    }
	}
}

export default new PDPModelMapper();
